import React, { useState } from 'react';

import './ServiceRequestDetails.scss';
import Diagnosis from 'src/components/Diagnosis/Diagnosis';
import Row from 'src/components/Row';
import Col from 'src/components/Col';
import { GenericDialogue } from 'src/components/Dialogue/Dialogue';
import NewBadge from 'src/components/NewBadge/NewBadge';
import AreaAssetGridSkeleton from 'src/pages/ServiceRequest/ServiceRequestDetails/skeletons/AreaAssetGridSkeleton';
import Button from 'src/components/Button/Button';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Search from 'src/components/Search/Search';
import {
  useChangeAssetAPI,
  UseChangeAssetAPIReturn,
} from './CustomHooks/useChangeAssetAPI';
import AreaAssetGrid, {
  AreaAssetGridSelection,
} from 'src/components/AreaAssetGrid/AreaAssetGrid';

interface ServiceRequestDetailsProps {
  serviceRequest: any;
  canEditAsset?: boolean;
  refreshData?: (serviceRequestId: string) => Promise<void> | void;
}

const ServiceRequestDetails = ({
  serviceRequest,
  canEditAsset = false,
  refreshData,
}: ServiceRequestDetailsProps) => {
  const {
    isOpen,
    isSubmitting,
    unitAreaAssets,
    isFetchingAssets,
    openDialogue,
    closeDialogue,
    handleChangeAsset,
  }: UseChangeAssetAPIReturn = useChangeAssetAPI();

  const [selection, setSelection] = useState<AreaAssetGridSelection | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState('');

  const handleConfirmAssetChange = async () => {
    if (!selection) {
      return;
    }

    const success: boolean = await handleChangeAsset(
      serviceRequest.id,
      selection.assetId
    );

    if (success) {
      await refreshData(serviceRequest.id);
    }
  };

  const handleClose = () => {
    closeDialogue();
    setSelection(null);
    setSearchQuery('');
  };

  const tagNames: any = (serviceRequest.tags ?? [])
    .map((tag: any) => tag?.name)
    .filter(Boolean);

  return (
    <div className="serviceRequestDetails">
      {serviceRequest.asset?.item?.name && (
        <Diagnosis
          asset={serviceRequest.asset.item.name}
          issueType={serviceRequest.issueType?.name}
          location={serviceRequest.asset?.name}
          expiresAt={
            serviceRequest.asset?.warrantDays < 60
              ? serviceRequest.asset?.warrantyExpiresAt
              : null
          }
          label={undefined}
          onEditAsset={
            canEditAsset
              ? () => openDialogue(serviceRequest.suite?.id)
              : undefined
          }
        />
      )}
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title={
          <>
            Change Asset <NewBadge className="change-asset-new-badge" />
          </>
        }
        description="Select a new room and asset for this service request."
        maxWidth="100rem"
        maxHeight="75vh"
        minHeight="75vh"
        subheader={
          <div className="change-asset-dialogue-subheader">
            <div className="change-asset-dialogue-selection">
              {selection ? (
                <>
                  <span className="change-asset-dialogue-selection-label">
                    Selected:
                  </span>
                  <CategoryPill
                    text={selection.assetName}
                    size="normal"
                    color="#454F5B"
                    textColor="white"
                  />
                  <span className="change-asset-dialogue-selection-area">
                    in {selection.areaName}
                  </span>
                </>
              ) : (
                <span className="change-asset-dialogue-selection-placeholder">
                  No asset selected yet
                </span>
              )}
            </div>
            <Search
              size="small"
              theme={undefined}
              placeholder="Search rooms or assets..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
            />
          </div>
        }
        footer={
          <Button
            color="teal"
            size="small"
            label="Confirm Asset"
            disabled={!selection || isSubmitting}
            onClick={handleConfirmAssetChange}
          />
        }
      >
        {isFetchingAssets ? (
          <AreaAssetGridSkeleton />
        ) : (
          <AreaAssetGrid
            areas={unitAreaAssets}
            searchQuery={searchQuery}
            selectedAssetId={selection?.assetId ?? null}
            isSubmitting={isSubmitting}
            onSelectAsset={setSelection}
          />
        )}
      </GenericDialogue>
      <Row>
        <Col xs={12}>
          <span className="serviceRequestDetails__label">Description</span>
          <p className="serviceRequestDetails__value">
            {serviceRequest.description}
          </p>
        </Col>
      </Row>
      {serviceRequest.asset?.serialNumber && (
        <Row>
          <Col xs={6}>
            <span className="serviceRequestDetails__label">Item ID</span>
            <p className="serviceRequestDetails__value">
              {serviceRequest.asset.serialNumber}
            </p>
          </Col>
        </Row>
      )}
      <br />
      {tagNames.length > 0 && (
        <Row>
          <Col xs={12}>
            <strong>Tags: </strong>
            {tagNames.join(', ')}
          </Col>
        </Row>
      )}
      <br />
    </div>
  );
};

export default ServiceRequestDetails;
